import type { Vec2 } from './vec';
import { sub, scale, dot, len, normalize } from './vec';

/**
 * 2×3 affine matrix stored as [a, c, e, b, d, f], applied as
 * (x,y) ↦ (a·x + c·y + e, b·x + d·y + f).
 *
 * Matrix structure:
 *   L = [[a, c],    t = (e, f)
 *        [b, d]]
 */
export type Affine = readonly [number, number, number, number, number, number];

/**
 * Create a translation matrix.
 */
export function translation(v: Vec2): Affine {
  return [1, 0, v.x, 0, 1, v.y];
}

/**
 * Create a rotation matrix rotating by deg degrees CCW about a point.
 */
export function rotation(aboutPoint: Vec2, deg: number): Affine {
  const rad = (deg * Math.PI) / 180;
  const cos = Math.cos(rad);
  const sin = Math.sin(rad);

  // L = [[cos, -sin], [sin, cos]]
  const a = cos;
  const c = -sin;
  const b = sin;
  const d = cos;

  // t = about - L·about
  const lapplied = {
    x: a * aboutPoint.x + c * aboutPoint.y,
    y: b * aboutPoint.x + d * aboutPoint.y,
  };
  const e = aboutPoint.x - lapplied.x;
  const f = aboutPoint.y - lapplied.y;

  return [a, c, e, b, d, f];
}

/**
 * Create a reflection matrix across a line through axisPoint with direction axisDir.
 */
export function reflection(axisPoint: Vec2, axisDir: Vec2): Affine {
  const n = normalize(axisDir);
  const nx = n.x;
  const ny = n.y;

  // Householder reflection: L = [[nx²-ny², 2nxny], [2nxny, ny²-nx²]]
  const a = nx * nx - ny * ny;
  const c = 2 * nx * ny;
  const b = 2 * nx * ny;
  const d = ny * ny - nx * nx;

  // t = axisPoint - L·axisPoint
  const lapplied = {
    x: a * axisPoint.x + c * axisPoint.y,
    y: b * axisPoint.x + d * axisPoint.y,
  };
  const e = axisPoint.x - lapplied.x;
  const f = axisPoint.y - lapplied.y;

  return [a, c, e, b, d, f];
}

/**
 * Create a dilation (scaling) matrix with center and scalar k.
 */
export function dilation(center: Vec2, k: number): Affine {
  // L = k·I
  const a = k;
  const c = 0;
  const b = 0;
  const d = k;

  // t = center - L·center = (1-k)·center
  const e = (1 - k) * center.x;
  const f = (1 - k) * center.y;

  return [a, c, e, b, d, f];
}

/**
 * Compose two affine transformations: (second ∘ first).
 * apply(compose(B,A), p) === apply(B, apply(A, p))
 */
export function compose(second: Affine, first: Affine): Affine {
  const [a1, c1, e1, b1, d1, f1] = first;
  const [a2, c2, e2, b2, d2, f2] = second;

  // Multiply linear parts: L2 · L1
  const a = a2 * a1 + c2 * b1;
  const c = a2 * c1 + c2 * d1;
  const b = b2 * a1 + d2 * b1;
  const d = b2 * c1 + d2 * d1;

  // t = t2 + L2·t1
  const e = e2 + a2 * e1 + c2 * f1;
  const f = f2 + b2 * e1 + d2 * f1;

  return [a, c, e, b, d, f];
}

/**
 * Apply an affine transformation to a point.
 */
export function apply(m: Affine, p: Vec2): Vec2 {
  const [a, c, e, b, d, f] = m;
  return {
    x: a * p.x + c * p.y + e,
    y: b * p.x + d * p.y + f,
  };
}

export type TransformClass =
  | { kind: 'identity' }
  | { kind: 'translation'; vector: Vec2 }
  | { kind: 'rotation'; center: Vec2; deg: number }
  | { kind: 'reflection'; axisPoint: Vec2; axisDir: Vec2 }
  | { kind: 'glide'; axisPoint: Vec2; axisDir: Vec2; vector: Vec2 }
  | { kind: 'dilation'; center: Vec2; k: number }
  | { kind: 'other' };

/**
 * Classify an affine transformation.
 */
export function classify(m: Affine, eps: number): TransformClass {
  const [a, c, e, b, d, f] = m;

  // Extract linear part and translation
  const det = a * d - b * c;
  const t: Vec2 = { x: e, y: f };

  // Check if determinant ≈ 1 (orientation-preserving)
  if (Math.abs(det - 1) <= eps) {
    // Check if L ≈ I (identity linear part)
    if (
      Math.abs(a - 1) <= eps &&
      Math.abs(b) <= eps &&
      Math.abs(c) <= eps &&
      Math.abs(d - 1) <= eps
    ) {
      // Pure translation or identity
      if (Math.abs(e) <= eps && Math.abs(f) <= eps) {
        return { kind: 'identity' };
      }
      return { kind: 'translation', vector: { x: e, y: f } };
    }

    // Rotation: solve (I - L)·center = t
    const degreesToCheck = Math.atan2(b, a) * (180 / Math.PI);
    const center = solveLinearFixed(a, c, b, d, e, f);

    if (center !== null) {
      return {
        kind: 'rotation',
        center,
        deg: degreesToCheck,
      };
    }

    return { kind: 'other' };
  }

  // Check if determinant ≈ -1 (reflection or glide)
  if (Math.abs(det + 1) <= eps) {
    // Find the eigenvector for eigenvalue +1 (reflection axis direction)
    const axisDir = findReflectionAxis(a, c, b, d);

    if (axisDir === null) {
      return { kind: 'other' };
    }

    // Decompose translation into components parallel and perpendicular to axis
    const tParallel = dot(t, axisDir);
    const tPerp = {
      x: t.x - tParallel * axisDir.x,
      y: t.y - tParallel * axisDir.y,
    };

    // axisPoint is the midpoint in the perpendicular direction
    const axisPoint = scale(tPerp, 0.5);

    if (Math.abs(tParallel) <= eps) {
      return {
        kind: 'reflection',
        axisPoint,
        axisDir,
      };
    }

    // Glide reflection: translation along the mirror line
    return {
      kind: 'glide',
      axisPoint,
      axisDir,
      vector: scale(axisDir, tParallel),
    };
  }

  // Check if L ≈ k·I (dilation)
  if (
    Math.abs(a - d) <= eps &&
    Math.abs(b) <= eps &&
    Math.abs(c) <= eps &&
    Math.abs(a - 1) > eps &&
    Math.abs(a + 1) > eps
  ) {
    const k = a;
    // center = t / (1 - k)
    if (Math.abs(1 - k) > eps) {
      const center = {
        x: e / (1 - k),
        y: f / (1 - k),
      };
      return {
        kind: 'dilation',
        center,
        k,
      };
    }
  }

  return { kind: 'other' };
}

/**
 * Solve (I - L)·x = t for x, where L = [[a,c],[b,d]].
 * This finds the fixed point of the transformation.
 */
function solveLinearFixed(
  a: number,
  c: number,
  b: number,
  d: number,
  e: number,
  f: number,
): Vec2 | null {
  // I - L = [[1-a, -c], [-b, 1-d]]
  const ia = 1 - a;
  const ic = -c;
  const ib = -b;
  const id = 1 - d;

  // Solve [[ia, ic], [ib, id]]·[x, y] = [e, f]
  const det = ia * id - ic * ib;

  if (Math.abs(det) < 1e-15) {
    return null;
  }

  const x = (e * id - ic * f) / det;
  const y = (ia * f - e * ib) / det;

  return { x, y };
}

/**
 * Find the unit eigenvector for eigenvalue +1 of the reflection matrix.
 * For a reflection matrix with det=-1, there is always an eigenvector for λ=+1.
 */
function findReflectionAxis(a: number, c: number, b: number, d: number): Vec2 | null {
  // (L - I)·v = 0, so [[a-1, c], [b, d-1]]·v = 0
  const ma = a - 1;
  const mc = c;
  const mb = b;
  const md = d - 1;

  // Try to find a non-zero row
  const row1 = { x: ma, y: mc };
  const row2 = { x: mb, y: md };

  const len1 = len(row1);
  const len2 = len(row2);

  let eigenvector: Vec2 | null = null;

  if (len1 > 1e-9) {
    // Perpendicular to first row: rotate by 90°
    eigenvector = normalize({ x: -mc, y: ma });
  } else if (len2 > 1e-9) {
    // Perpendicular to second row
    eigenvector = normalize({ x: -md, y: mb });
  } else {
    // Both rows are zero; shouldn't happen for reflection
    return null;
  }

  return eigenvector;
}

/**
 * Find all mirror lines that map a multiset of points onto itself.
 */
export function symmetryAxes(points: readonly Vec2[]): { point: Vec2; dir: Vec2 }[] {
  if (points.length === 0) {
    return [];
  }

  // Compute centroid
  const centroid: Vec2 = {
    x: points.reduce((sum, p) => sum + p.x, 0) / points.length,
    y: points.reduce((sum, p) => sum + p.y, 0) / points.length,
  };

  const candidates: { point: Vec2; dir: Vec2 }[] = [];

  // Candidate 1: axes through centroid and each vertex
  for (const p of points) {
    const dir = normalize(sub(p, centroid));
    if (len(dir) > 1e-9) {
      candidates.push({ point: centroid, dir });
    }
  }

  // Candidate 2: axes through centroid and each edge midpoint
  for (let i = 0; i < points.length; i++) {
    const p1 = points[i]!;
    const p2 = points[(i + 1) % points.length]!;
    const midpoint = {
      x: (p1.x + p2.x) / 2,
      y: (p1.y + p2.y) / 2,
    };
    const dir = normalize(sub(midpoint, centroid));
    if (len(dir) > 1e-9) {
      candidates.push({ point: centroid, dir });
    }
  }

  // Filter to only valid axes
  const validAxes: { point: Vec2; dir: Vec2 }[] = [];
  const tolerance = 1e-6;

  for (const candidate of candidates) {
    if (isSymmetryAxis(points, candidate, tolerance)) {
      // Check if already in list (avoid duplicates)
      // Two axes are the same if points are the same and directions are parallel (including opposite)
      const isDuplicate = validAxes.some((axis) => areSameAxis(axis, candidate, tolerance));

      if (!isDuplicate) {
        validAxes.push(candidate);
      }
    }
  }

  return validAxes;
}

/**
 * Check if two axes represent the same line.
 */
function areSameAxis(
  axis1: { point: Vec2; dir: Vec2 },
  axis2: { point: Vec2; dir: Vec2 },
  tolerance: number,
): boolean {
  // Points should match
  if (
    Math.abs(axis1.point.x - axis2.point.x) > tolerance ||
    Math.abs(axis1.point.y - axis2.point.y) > tolerance
  ) {
    return false;
  }

  // Directions should be parallel (same or opposite)
  const d1 = normalize(axis1.dir);
  const d2 = normalize(axis2.dir);

  const sameDir = Math.abs(d1.x - d2.x) <= tolerance && Math.abs(d1.y - d2.y) <= tolerance;
  const oppositeDir = Math.abs(d1.x + d2.x) <= tolerance && Math.abs(d1.y + d2.y) <= tolerance;

  return sameDir || oppositeDir;
}

/**
 * Check if a transformed multiset matches the original multiset injectively.
 * Each original point may be matched at most once.
 */
function multisetMatches(
  transformed: readonly Vec2[],
  original: readonly Vec2[],
  tolerance: number,
): boolean {
  if (transformed.length !== original.length) {
    return false;
  }

  const used: boolean[] = [];
  for (let i = 0; i < original.length; i++) {
    used.push(false);
  }

  for (const tp of transformed) {
    let found = false;
    for (let i = 0; i < original.length; i++) {
      if (!used[i]) {
        const op = original[i]!;
        if (Math.abs(op.x - tp.x) <= tolerance && Math.abs(op.y - tp.y) <= tolerance) {
          used[i] = true;
          found = true;
          break;
        }
      }
    }
    if (!found) {
      return false;
    }
  }

  return true;
}

/**
 * Check if a line is a symmetry axis for a point set.
 */
function isSymmetryAxis(
  points: readonly Vec2[],
  axis: { point: Vec2; dir: Vec2 },
  tolerance: number,
): boolean {
  const reflected = points.map((p) => apply(reflection(axis.point, axis.dir), p));
  return multisetMatches(reflected, points, tolerance);
}

/**
 * Find the largest rotation order of a point set about its centroid.
 */
export function rotationOrder(points: readonly Vec2[]): number {
  if (points.length === 0) {
    return 1;
  }

  // Compute centroid
  const centroid: Vec2 = {
    x: points.reduce((sum, p) => sum + p.x, 0) / points.length,
    y: points.reduce((sum, p) => sum + p.y, 0) / points.length,
  };

  const tolerance = 1e-6;

  // Try each possible order from 2 to points.length
  for (let k = points.length; k >= 2; k--) {
    const angleStep = 360 / k;
    const rotated = points.map((p) => apply(rotation(centroid, angleStep), p));

    if (multisetMatches(rotated, points, tolerance)) {
      return k;
    }
  }

  return 1;
}
